// src/pages/admin/Categories.jsx - MOBILE RESPONSIVE FIXED
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  Snackbar,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { Add, Edit, Delete, Close, Check, MoreVert, Refresh } from '@mui/icons-material';
import { adminApi } from '../../api/adminApi';

const Categories = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', displayOrder: 0 });
  const [formErrors, setFormErrors] = useState({});

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const fetchCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.getCategories(true);
      const categoriesData = response?.categories || [];
      setCategories(categoriesData);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleMenuOpen = (event, category) => {
    setAnchorEl(event.currentTarget);
    setSelectedCategory(category);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCategory(null);
  };

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({ 
        name: category.name || '', 
        displayOrder: category.displayOrder || 0 
      });
    } else {
      setEditingCategory(null);
      setFormData({ name: '', displayOrder: 0 });
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCategory(null);
    setFormData({ name: '', displayOrder: 0 });
    setFormErrors({});
    setError(null);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name || formData.name.trim() === '') {
      errors.name = 'Category name is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      const dataToSave = {
        name: formData.name.trim(),
        displayOrder: parseInt(formData.displayOrder) || 0,
      };

      if (editingCategory) {
        await adminApi.updateCategory(editingCategory.id, dataToSave);
        setSuccess('Category updated successfully!');
      } else {
        await adminApi.createCategory(dataToSave);
        setSuccess('Category created successfully!');
      }
      handleCloseDialog();
      await fetchCategories();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save category');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await adminApi.deleteCategory(id);
        setSuccess('Category deleted successfully!');
        await fetchCategories();
        setTimeout(() => setSuccess(null), 3000);
        handleMenuClose();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete category');
      }
    }
  };

  const handleToggleStatus = async (category) => {
    try {
      const updatedStatus = !category.isActive;
      await adminApi.updateCategory(category.id, { isActive: updatedStatus });
      setSuccess(`Category "${category.name}" ${updatedStatus ? 'activated' : 'deactivated'}`);
      await fetchCategories();
      setTimeout(() => setSuccess(null), 3000);
      handleMenuClose();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to toggle category status');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  const hasCategories = categories && categories.length > 0;

  return (
    <Box sx={{ pb: { xs: 8, sm: 10 } }}> {/* Added padding bottom to avoid footer overlap */}
      
      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              Categories
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage product categories ({categories.length} total)
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} width={{ xs: '100%', sm: 'auto' }}>
            <Button 
              variant="outlined" 
              startIcon={<Refresh />} 
              onClick={fetchCategories}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
              sx={{ borderRadius: 2 }}
            >
              Add Category
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Mobile Card View */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {!hasCategories ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
              <Typography color="text.secondary" variant="body1">
                No categories found
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
                sx={{ mt: 2 }}
              >
                Create your first category
              </Button>
            </Paper>
          ) : (
            categories.map((category) => (
              <Card key={category.id} sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {category.name}
                        </Typography>
                        {category.slug && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            slug: {category.slug}
                          </Typography>
                        )}
                      </Box>
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, category)}>
                        <MoreVert />
                      </IconButton>
                    </Stack>
                    
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Order: {category.displayOrder || 0}
                      </Typography>
                      <Chip
                        icon={category.isActive ? <Check /> : <Close />}
                        label={category.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={category.isActive ? 'success' : 'error'}
                        variant={category.isActive ? 'filled' : 'outlined'}
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      ) : (
        /* Desktop Table View */
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Name</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Display Order</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Status</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2" fontWeight="bold">Actions</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!hasCategories ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 6 }}>
                      <Typography color="text.secondary" variant="body1">
                        No categories found
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => handleOpenDialog()}
                        sx={{ mt: 2 }}
                      >
                        Create your first category
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  categories.map((category) => (
                    <TableRow key={category.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="500">
                          {category.name}
                        </Typography>
                        {category.slug && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            slug: {category.slug}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{category.displayOrder || 0}</TableCell>
                      <TableCell>
                        <Chip
                          icon={category.isActive ? <Check /> : <Close />}
                          label={category.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={category.isActive ? 'success' : 'error'}
                          variant={category.isActive ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, category)}>
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => { handleOpenDialog(selectedCategory); handleMenuClose(); }}>
          <Edit fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => handleToggleStatus(selectedCategory)}>
          {selectedCategory?.isActive ? (
            <>
              <Close fontSize="small" sx={{ mr: 1 }} /> Deactivate
            </>
          ) : (
            <>
              <Check fontSize="small" sx={{ mr: 1 }} /> Activate
            </>
          )}
        </MenuItem>
        <MenuItem onClick={() => handleDelete(selectedCategory?.id)} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, m: { xs: 1, sm: 2 } }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            {editingCategory ? 'Edit Category' : 'Add New Category'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {editingCategory 
              ? 'Update the category details below' 
              : 'Fill in the details to create a new category'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Category Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              error={!!formErrors.name}
              helperText={formErrors.name}
              placeholder="e.g., Electronics, Clothing, Books"
              autoFocus
            />
            <TextField
              fullWidth
              label="Display Order"
              type="number"
              value={formData.displayOrder}
              onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
              helperText="Lower numbers appear first in listings"
              InputProps={{
                inputProps: { min: 0 }
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={handleCloseDialog} variant="outlined" fullWidth={isMobile}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSave}
            disabled={!formData.name || formData.name.trim() === ''}
            fullWidth={isMobile}
            sx={{ borderRadius: 2 }}
          >
            {editingCategory ? 'Update Category' : 'Create Category'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Categories;